import { Dialog, DialogContent, DialogTitle } from '@heidi/design-system/dialog';
import { useAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import type {
  DictateDownloadAttempt,
  DictateDownloadTarget,
  DictateInstallerChannel,
} from '../../contracts/desktop-download';
import type { DictateDesktopDownloadAnalyticsPorts } from '../../contracts/desktop-download-analytics.types';
import { isDictateDesktopDownloadDialogOpenAtom } from './atoms';
import { desktopDownloadMessages as messages } from './messages';

const DesktopDownloadDialogContent = dynamic(
  () =>
    import('./desktop-download-dialog-content').then(
      (mod) => mod.DesktopDownloadDialogContent
    ),
  { ssr: false, loading: () => <div className="h-[372px]" /> }
);

export type DesktopDownloadDialogProps = DictateDesktopDownloadAnalyticsPorts & {
  /** The installer the app resolved for this browser. */
  target: DictateDownloadTarget;
  /** Whether the app handed the automatic download to the browser. */
  attempt: DictateDownloadAttempt;
  /** The CrabNebula channel for the user's authenticated region. */
  channel: DictateInstallerChannel;
  /** Staging builds link the staging CrabNebula app. */
  staging: boolean;
};

const titleFor = (target: DictateDownloadTarget, attempt: DictateDownloadAttempt) => {
  if (target.os === 'unsupported') return messages.unsupportedTitle;
  return attempt === 'failed' ? messages.failedTitle : messages.startingTitle;
};

/**
 * The Dictate install-help dialog. The shell owns the open state and the
 * `viewed` emit; the body loads once the open animation settles. The app
 * host starts the download and binds target, attempt, channel, environment and
 * the two emits — the dialog itself never requests an installer.
 */
export const DictateDesktopDownloadDialog = ({
  target,
  attempt,
  channel,
  staging,
  onViewed,
  onInstallerClicked,
}: DesktopDownloadDialogProps) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useAtom(isDictateDesktopDownloadDialogOpenAtom);
  const [isDialogVisible, setIsDialogVisible] = useState(false);

  useEffect(() => {
    if (isOpen) onViewed();
  }, [isOpen, onViewed]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={setIsOpen}
      onOpenChangeComplete={setIsDialogVisible}
    >
      <DialogContent
        // Figma `6051:18468`: 672px wide, 24px corners; icons overlap the top
        // edge, so overflow stays visible.
        className="w-[calc(100%-2rem)] max-w-[672px] gap-0 overflow-visible rounded-3xl p-0 sm:rounded-3xl"
        data-testid="dictate-desktop-download-dialog"
      >
        {isOpen && isDialogVisible ? (
          <DesktopDownloadDialogContent
            target={target}
            attempt={attempt}
            channel={channel}
            staging={staging}
            onInstallerClicked={onInstallerClicked}
          />
        ) : (
          <>
            {/* Radix requires a title in the tree before the body mounts. */}
            <DialogTitle className="sr-only">
              {intl.formatMessage(titleFor(target, attempt))}
            </DialogTitle>
            <div className="h-[372px]" />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
